import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import {
  BoundsCalculator,
  DefaultCamera,
  FitCalculator,
  MovementCalculator,
  PositionCalculator,
  SelectionCallback,
} from 'features/assetViz/cameras/DefaultCamera'
import { HoodBendSurface3D } from 'features/assetViz/objects/surfaces/HoodBendSurface3D'
import { HoodStraightSurface3D } from 'features/assetViz/objects/surfaces/HoodStraightSurface3D'
import { SceneFactoryConfig } from 'features/assetViz/scenes/sceneFactory'
import React from 'react'
import {
  GeckoAssetModel,
  GeckoAssetModelType,
  GeckoComponentModelType,
  GeckoHoodBend,
  GeckoHoodStraight,
  GeckoMeasurementUnit,
  TUBESPACING_DEFAULT,
} from 'types'

export const HoodSceneConfig: SceneFactoryConfig = {
  name: 'Hood',
  assetModels: [GeckoAssetModelType.Hood],
  componentModels: [
    GeckoComponentModelType.HoodStraight,
    GeckoComponentModelType.HoodBend,
  ],
  sceneFunc: (props) => {
    if (
      props.componentModel.type === GeckoComponentModelType.HoodStraight ||
      props.componentModel.type === GeckoComponentModelType.HoodBend
    ) {
      return (
        <HoodScene
          assetModel={props.assetModel}
          componentModel={props.componentModel}
        />
      )
    }
    return null
  },
}

/**
 * A scene depicting a hood system which can wrap around the x-axis.
 * @param assetModel      The model used to represent the whole asset
 * @param componentModel  The model for the component in question
 */
export const HoodScene: React.FC<{
  assetModel: GeckoAssetModel
  componentModel: GeckoHoodStraight | GeckoHoodBend
}> = ({ componentModel: selectedModel }) => {
  //----------------------------------------------------------------------------
  const { show3D, componentDataFrames } = useAssetVizInternal()

  const tubeSpacing = React.useMemo(() => {
    if (selectedModel.type === GeckoComponentModelType.HoodStraight) {
      return selectedModel.tubeSpacing ?? TUBESPACING_DEFAULT
    }

    return undefined
  }, [selectedModel])

  const componentBounds = React.useMemo(() => {
    const componentBounds = selectedModel.componentBounds
    const expectedX =
      selectedModel.type === GeckoComponentModelType.HoodStraight
        ? GeckoMeasurementUnit.Tubes
        : GeckoMeasurementUnit.Meters

    if (
      componentBounds.x.unit !== expectedX ||
      componentBounds.y.unit !== GeckoMeasurementUnit.Meters
    ) {
      throw new Error(
        `Asset model should always be in ${expectedX.id} and meters,
        but the x range is in ${componentBounds.x.unit.id} and y range is in
        ${componentBounds.y.unit.id}`
      )
    }

    return componentBounds
  }, [selectedModel.componentBounds, selectedModel.type])

  const radius = React.useMemo(() => {
    if (selectedModel.type === GeckoComponentModelType.HoodStraight) {
      return selectedModel.radius
    } else if (selectedModel.type === GeckoComponentModelType.HoodBend) {
      return selectedModel.majorRadius
    }
    return undefined
  }, [selectedModel])

  //----------------------------------------------------------------------------

  const onComponentSelected = React.useCallback<SelectionCallback>(
    () =>
      componentDataFrames
        ?.at(0)
        ?.data?.geometry?.project(componentBounds.space) ?? componentBounds,
    [componentBounds, componentDataFrames]
  )

  const getCameraBounds = React.useCallback<BoundsCalculator>(() => {
    return { rect: componentBounds, doWrapX: show3D }
  }, [componentBounds, show3D])

  const getCameraPosition = React.useCallback<PositionCalculator>(
    ({ componentX, componentY, distToComponent }) => {
      const relX = tubeSpacing
        ? tubeSpacing.num * (componentX - (componentBounds.start.x.num ?? 0))
        : componentX - (componentBounds.start.x.num ?? 0)
      const relY = componentY - (componentBounds.start.y.num ?? 0)

      if (!show3D || !selectedModel || !radius) {
        return {
          position: [relX, relY, distToComponent],
          rotation: [0, 0, 0],
        }
      }

      const r = distToComponent + radius.num
      let angle = 2.0 * Math.PI * (relX / componentBounds.width.num)
      if (selectedModel instanceof GeckoHoodBend) {
        return {
          position: [
            r * Math.sin(angle),
            relY,
            r * Math.cos(angle) + selectedModel.majorRadius.num,
          ],
          rotation: [0, angle, 0],
        }
      }

      const tubePosition = componentX - (componentBounds.start.x.num ?? 0)
      const circumference = radius.num * 2 * Math.PI
      const spatialX = tubeSpacing
        ? tubePosition * tubeSpacing.num
        : tubePosition
      angle = 2.0 * Math.PI * (spatialX / circumference)
      const distToAxis = radius.num + distToComponent

      return {
        position: [
          Math.sin(angle) * distToAxis,
          relY,
          Math.cos(angle) * distToAxis,
        ],
        rotation: [0, angle, 0],
      }
    },
    [
      selectedModel,
      tubeSpacing,
      componentBounds.start.x.num,
      componentBounds.start.y.num,
      componentBounds.width.num,
      show3D,
      radius,
    ]
  )

  const getMovementForCursorDelta = React.useCallback<MovementCalculator>(
    ([x, y]) => {
      if (selectedModel.type === GeckoComponentModelType.HoodBend && show3D) {
        return [x, y]
      } else if (tubeSpacing) {
        return [x / tubeSpacing.num, y]
      }
      return [x, y]
    },
    [selectedModel.type, show3D, tubeSpacing]
  )

  const getCameraDimensionsFit = React.useCallback<FitCalculator>(
    (rect) => {
      const factor = tubeSpacing ? tubeSpacing.num : 1

      if (!radius) return [factor * rect.width.num, rect.height.num]

      return [
        Math.min(factor * rect.width.num, radius.num * 2.0),
        rect.height.num,
      ]
    },
    [radius, tubeSpacing]
  )
  //----------------------------------------------------------------------------

  return (
    <>
      <DefaultCamera
        onComponentSelected={onComponentSelected}
        getCameraBounds={getCameraBounds}
        getCameraPosition={getCameraPosition}
        getMovementForCursorDelta={getMovementForCursorDelta}
        getCameraDimensionsFit={getCameraDimensionsFit}
      />
      {/* Hood component surface --------------------------------------- */}
      {selectedModel.type === GeckoComponentModelType.HoodStraight ? (
        <HoodStraightSurface3D model={selectedModel} />
      ) : selectedModel.type === GeckoComponentModelType.HoodBend ? (
        <HoodBendSurface3D model={selectedModel} />
      ) : null}
    </>
  )
}
